<template>
  <Modal name="CREATE_EDIT_ITEM_MODAL" :title="title" :width="600" actions>
    <template v-slot:body>
      <v-form ref="itemForm" v-model="valid" lazy-validation>
        <v-text-field
          v-model="name"
          @keydown.enter.prevent="validate"
          :label="$t('item.label.name')"
          :rules="!isItemCreated ? rules.required : []"
          :loading="isItemLoading"
          ref="itemNameInput"
          hide-details="auto"
          outlined
        />

        <v-spacer class="my-4" />

        <v-autocomplete
          v-model="category"
          :items="results"
          :rules="rules.required"
          :search-input.sync="search"
          :loading="isCategoryLoading"
          :label="$t('item.label.category')"
          :no-data-text="$t('table.noResults')"
          append-icon="mdi-magnify"
          class="realtySearch"
          item-value="id"
          item-text="name"
          hide-details="auto"
          return-object
          outlined
        />

        <v-spacer class="my-4" />

        <v-textarea
          v-model="description"
          @keydown.enter.prevent="validate"
          :label="$t('item.label.description')"
          :rules="rules.required"
          :loading="isItemLoading"
          hide-details="auto"
          text-area
          outlined
        />

        <div v-if="isItemCreated">
          <v-spacer class="my-4" />

          <v-row>
            <v-col cols="12" md="4">
              <HAmount
                @input="setBasePriceAmount"
                :amount="newBasePrice"
                :label="$t('itemPrice.label.basePrice')"
                fullWidth
              />
            </v-col>
            <v-col cols="12" md="4">
              <HAmount
                @input="setComboPriceAmount"
                :amount="newComboPrice"
                :label="$t('itemPrice.label.comboPrice')"
                fullWidth
              />
            </v-col>
            <v-col cols="12" md="4">
              <p class="mt-n2 mb-0 caption">
                {{ $t('itemPrice.label.comboEnabler') }}
              </p>
              <v-switch
                v-model="newComboEnabler"
                :label="
                  comboEnabler
                    ? $t('itemPrice.action.comboIsEnabled')
                    : $t('itemPrice.action.comboIsNotEnabled')
                "
                inset
              />
            </v-col>
          </v-row>
        </div>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="isItemLoading"
        elevation="0"
        class="mr-5"
        large
        text
      >
        {{ $t('item.action.cancel') }}
      </v-btn>

      <!-- create item -->
      <v-btn
        v-if="!isItemCreated"
        @click="createItem"
        :loading="isItemLoading"
        color="primary"
        elevation="0"
        large
      >
        {{ item ? $t('item.action.edit') : $t('item.action.create') }}
      </v-btn>
      <!-- /create item -->

      <!-- add price on it -->
      <v-btn
        v-else
        @click="addPriceToItem"
        :loading="isItemPriceLoading"
        color="primary"
        elevation="0"
        large
      >
        {{ $t('itemPrice.action.addPrice') }}
      </v-btn>
      <!-- /add price on it -->
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { required } from '@/util/defaultValidationRules'

import Modal from '@/components/app/Modal'
import HAmount from '@/components/app/HAmount'

export default {
  name: 'ItemCreateEdit',

  components: {
    Modal,
    HAmount,
  },

  props: {
    itemPriceId: {
      type: [String, Number],
      default: null,
    },

    isAnUpdate: {
      type: Boolean,
      default: false,
    },

    isOnlyForCreateAnItem: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      id: null,
      name: null,
      description: null,
      category: null,

      newBasePrice: 0,
      newComboPrice: 0,
      newComboEnabler: false,

      results: [],
      valid: false,
      search: null,
      isLoading: false,
      _timerId: null,

      rules: {
        required,
      },
    }
  },

  watch: {
    item(item) {
      Promise.resolve(item)
        .then(() => {
          const currentId = item?.id

          if (currentId) this.fillItemFields()
          else this.resetItemFields()
        })
        .then(() => {
          return this.$refs?.itemNameInput?.$el?.querySelector('input')
        })
        .then((input) => input?.focus())
    },

    search(term) {
      clearTimeout(this._timerId)

      this._timerId = setTimeout(() => {
        this.searchCategories(term).then(() => {
          this.results = this.categorySearchResults
        })
      }, 500)
    },
  },

  computed: {
    ...mapGetters([
      'item',
      'categorySearchResults',
      'isItemLoading',
      'isItemPriceLoading',
      'isCategoryLoading',
      'isItemCreated',
      'priceTableId',
      'categories',
    ]),

    title() {
      return this.isItemLoading
        ? this.$t('item.action.loading')
        : this.item
        ? this.$t('item.action.edit')
        : this.$t('item.action.create')
    },
  },

  methods: {
    ...mapMutations(['closeAllModals']),
    ...mapActions([
      'fetchCategories',
      'searchCategories',
      'createEditItem',
      'addItemPrice',
      'updateItemPrice',
    ]),

    createItem() {
      if (this.$refs.itemForm.validate()) {
        const item = {
          id: this.id ? this.id : null,
          name: this.name,
          description: this.description,
          categoryId: this.category.id,
        }

        this.createEditItem(item)
      }
    },

    addPriceToItem() {
      if (this.$refs.itemForm.validate()) {
        const itemPrice = {
          id: this.itemPriceId,
          itemId: this.item?.id,
          priceTableId: this.priceTableId,
          comboPrice: this.newComboPrice,
          basePrice: this.newBasePrice,
          comboEnabler: this.newComboEnabler,
        }

        this.isAnUpdate
          ? this.updateItemPrice(itemPrice)
          : this.addItemPrice(itemPrice)
      }
    },

    fillItemFields() {
      Promise.resolve(this.item).then(() => {
        this.id = this.item ? this.item.id : null
        this.name = this.item ? this.item.name : null
        this.description = this.item ? this.item.description : null
        this.categoryId = this.item ? this.item.category.id : null

        this.results = [
          {
            id: this.item.category.id,
            name: this.item.category.name,
          },
        ]

        this.category = this.results[0]
      })
    },

    resetItemFields() {
      this.id = null
      this.name = null
      this.category = null
      this.categoryId = null
      this.description = null

      this.$nextTick(() => {
        this.$refs.itemForm && this.$refs.itemForm.resetValidation()
      })
    },

    setBasePriceAmount(value) {
      const amount = value ? value.replace(/\D/g, '') : 0
      this.newBasePrice = parseInt(amount, 10)
    },

    setComboPriceAmount(value) {
      const amount = value ? value.replace(/\D/g, '') : 0
      this.newComboPrice = parseInt(amount, 10)
    },
  },

  mounted() {
    this.fetchCategories().then(() => {
      this.results = this.categories
    })
  },
}
</script>
